/**
 * Patch notes — zmeny na portáli a v hrách (`changelog.ts`), zoskupené podľa
 * oblasti: prehľad (posledné zmeny každej oblasti), stránka jednej oblasti
 * (`#/patch-notes/<oblasť>` — len jej zmeny) a celá história chronologicky.
 * Kompaktný prehľad (`ChangelogPreview`) visí aj na titulnej stránke portálu.
 */

import { useEffect } from 'react';

import {
  CHANGELOG,
  KIND_LABEL,
  SCOPES,
  SCOPE_LABEL,
  entriesFor,
  formatDate,
  isScope,
  markSeen,
  type ChangeEntry,
  type ChangeScope,
} from './changelog.js';

/** `all` = celá história chronologicky, `null` = prehľad po oblastiach. */
type View = ChangeScope | 'all' | null;

interface Props {
  scope: string | null;
  onScope: (scope: string | null) => void;
}

/** Oblasti, ktoré majú aspoň jeden záznam (hra bez patch notes sa neukazuje). */
const ACTIVE = SCOPES.filter((scope) => entriesFor(scope).length > 0);

export function Changelog({ scope, onScope }: Props) {
  useEffect(markSeen, []);
  const view: View = scope === 'all' ? 'all' : isScope(scope) ? scope : null;

  return (
    <div className="changelog">
      <section className="portal__intro">
        <span className="tag tag--primary">Hearth &amp; Table</span>
        <h1 className="hero__title">Patch notes</h1>
        <p className="hero__lead">Čo pribudlo a čo sa opravilo na portáli a v jednotlivých hrách — najnovšie hore.</p>
      </section>

      <nav className="notesTabs" aria-label="Oblasti patch notes">
        <button type="button" className={`notesTabs__tab ${view === null ? 'is-active' : ''}`} onClick={() => onScope(null)}>
          Prehľad
        </button>
        {ACTIVE.map((item) => (
          <button
            key={item}
            type="button"
            className={`notesTabs__tab ${view === item ? 'is-active' : ''}`}
            onClick={() => onScope(item)}
          >
            {SCOPE_LABEL[item]} <span className="notesTabs__count">{entriesFor(item).length}</span>
          </button>
        ))}
        <button type="button" className={`notesTabs__tab ${view === 'all' ? 'is-active' : ''}`} onClick={() => onScope('all')}>
          Celá história
        </button>
      </nav>

      {view === null ? (
        <div className="notesOverview">
          {ACTIVE.map((item) => (
            <ScopeSummary key={item} scope={item} onOpen={() => onScope(item)} />
          ))}
        </div>
      ) : (
        <ol className="changelog__list">
          {(view === 'all' ? CHANGELOG : entriesFor(view)).map((entry) => (
            <li key={entry.id}>
              <Entry entry={entry} showScope={view === 'all'} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** Karta oblasti v prehľade: počet záznamov a posledné tri. */
function ScopeSummary({ scope, onOpen }: { scope: ChangeScope; onOpen: () => void }) {
  const entries = entriesFor(scope);
  const latest = entries[0];
  return (
    <article className="panel notesScope">
      <header className="notesScope__head">
        <h2 className="panel__title">{SCOPE_LABEL[scope]}</h2>
        <span className="notesScope__count">{entries.length} záznamov</span>
      </header>
      {latest ? (
        <p className="notesScope__latest">
          Naposledy <time dateTime={latest.date}>{formatDate(latest.date)}</time>
        </p>
      ) : null}
      <ul className="notesScope__list">
        {entries.slice(0, 3).map((entry) => (
          <li key={entry.id}>
            <span className="notesScope__title">{entry.title}</span>
            <span className="notesScope__kinds">
              {[...new Set(entry.changes.map((c) => c.kind))].map((kind) => (
                <span key={kind} className={`changeKind changeKind--${kind}`}>
                  {KIND_LABEL[kind]}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn--ghost btn--sm notesScope__open" onClick={onOpen}>
        Všetky patch notes: {SCOPE_LABEL[scope]} →
      </button>
    </article>
  );
}

function Entry({ entry, showScope }: { entry: ChangeEntry; showScope: boolean }) {
  return (
    <article className="panel changeEntry">
      <header className="changeEntry__head">
        <time dateTime={entry.date}>{formatDate(entry.date)}</time>
        <h2 className="panel__title">{entry.title}</h2>
      </header>
      <ul className="changeEntry__items">
        {entry.changes.map((change, i) => (
          <li key={i} className={`changeItem changeItem--${change.kind}`}>
            <span className="changeItem__tags">
              <span className={`changeKind changeKind--${change.kind}`}>{KIND_LABEL[change.kind]}</span>
              {showScope ? <span className="changeScope">{SCOPE_LABEL[change.scope]}</span> : null}
            </span>
            <span className="changeItem__text">{change.text}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

/**
 * Patch notes na titulnej stránke portálu — pre každú oblasť (portál, hry)
 * posledný záznam. Klik otvorí patch notes tej oblasti.
 */
/** Koľko posledných záznamov každej oblasti ukáže lístok na portáli. */
const PREVIEW_PER_SCOPE = 3;

export function ChangelogPreview({ onOpen }: { onOpen: (scope: string | null) => void }) {
  return (
    <section className="panel changePreview">
      <header className="panel__head">
        <h2 className="panel__title">Patch notes</h2>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpen(null)}>
          Všetky →
        </button>
      </header>
      <ul className="changePreview__list">
        {ACTIVE.map((scope) => (
          <li key={scope}>
            <span className="changePreview__scope">{SCOPE_LABEL[scope]}</span>
            {/* Posledné tri záznamy oblasti — lístok nech ukáže viac než jeden riadok. */}
            {entriesFor(scope)
              .slice(0, PREVIEW_PER_SCOPE)
              .map((entry) => (
                <span key={entry.id} className="changePreview__entry">
                  <button type="button" className="changePreview__title" onClick={() => onOpen(scope)}>
                    {entry.title}
                  </button>
                  <time dateTime={entry.date}>{formatDate(entry.date)}</time>
                </span>
              ))}
          </li>
        ))}
      </ul>
    </section>
  );
}
